import { Maybe } from '@csp/csp-common-model';
import { FC, PropsWithChildren, useMemo } from 'react';
import { AnalyticsClientFactory } from '../client/AnalyticsClientFactory';
import { useBrowserTabId } from '../hook/useBrowserTabId';
import { useIdentifyUser } from '../hook/useIdentifyUser';
import { EventProperties } from '../model/EventProperties';
import { AnalyticsContext } from './AnalyticsContext';

type Props = PropsWithChildren & {
  isEnabled?: boolean;
  identifyProperties?: EventProperties;
  userId?: string;
  locale?: string;
};

export const AnalyticsProvider: FC<Props> = ({ children, identifyProperties, isEnabled = true, userId, locale }) => {
  const browserTabId = useBrowserTabId();
  const anonymousId = browserTabId;

  const analyticsClient = useMemo(
    () =>
      AnalyticsClientFactory.create({ adapter: 'fetch', fetchOptions: { keepalive: true } }, { locale, anonymousId }),
    [locale, anonymousId],
  );

  const defaultProperties = useMemo(
    (): Record<string, string> => (browserTabId ? { browserTabId: browserTabId.toString() } : {}),
    [browserTabId],
  );

  const identifyPropsWithBrowserId = useMemo(
    (): Maybe<Record<string, string>> =>
      browserTabId ? { ...identifyProperties, browserTabId: browserTabId.toString() } : identifyProperties,
    [browserTabId, identifyProperties],
  );

  const { hasIdentifiedOnce } = useIdentifyUser({
    analyticsClient,
    userId,
    browserTabId,
    isEnabled,
    identifyProperties: identifyPropsWithBrowserId,
  });

  const isInitialized = useMemo(
    () => !!defaultProperties.browserTabId && hasIdentifiedOnce,
    [defaultProperties, hasIdentifiedOnce],
  );

  const analytics = useMemo(
    () => ({
      isEnabled,
      isInitialized,
      track: async (eventName: string, eventProperties = {}): Promise<void> => {
        if (!isInitialized) {
          console.warn(`Analytics client is not initialized. Track event was discarded.`);
          return;
        }
        if (!isEnabled) {
          return;
        }
        return analyticsClient.track(eventName, {
          userId,
          properties: {
            ...eventProperties,
            ...defaultProperties,
          },
        });
      },
      page: async (pageName: string, pageProperties = {}): Promise<void> => {
        if (!isInitialized) {
          console.warn(`Analytics client is not initialized. Page event was discarded.`);
          return;
        }
        if (!isEnabled) {
          return;
        }
        return analyticsClient.page(pageName, {
          userId,
          properties: {
            ...pageProperties,
            ...defaultProperties,
          },
        });
      },
    }),
    [isEnabled, analyticsClient, defaultProperties, userId, isInitialized],
  );

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};
